import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send } from "lucide-react";
import { API } from "../lib/api";
import { WA_CATALOG_URL } from "./WhatsAppButton";

const genSession = () => {
  let s = localStorage.getItem("bindni_chat_sid");
  if (!s) { s = "sid-" + Math.random().toString(36).slice(2, 10) + Date.now(); localStorage.setItem("bindni_chat_sid", s); }
  return s;
};

export const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey there! I'm Bindni AI. Ask me about products, sizing, shipping, care — or anything else you're curious about." }
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(genSession());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;

    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("Chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trimStart();
          if (data === "[DONE]") continue;
          setMessages((m) => {
            const next = [...m];
            next[next.length - 1] = { role: "assistant", content: (next[next.length - 1].content || "") + data };
            return next;
          });
        }
      }
    } catch (err) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", content: `I'm having trouble responding. Please open our WhatsApp Catalogue: ${WA_CATALOG_URL}` };
        return next;
      });
    } finally { setStreaming(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-brand-maroon text-brand-ivory px-5 py-3.5 rounded-full shadow-lg hover:bg-brand-maroon-deep transition-colors duration-200"
        aria-label="Open chat"
        data-testid="chatbot-toggle-btn"
      >
        <Sparkles size={18} strokeWidth={1.5} />
        <span className="text-xs tracking-[0.2em] uppercase font-medium">{open ? "Close" : "Ask Bindni"}</span>
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[92vw] sm:w-[380px] h-[520px] bg-brand-ivory shadow-2xl flex flex-col border border-brand-gold/40" data-testid="chatbot-panel">
          <div className="flex items-center justify-between px-5 py-4 bg-brand-maroon text-brand-ivory">
            <div>
              <p className="font-serif-brand text-lg leading-none">Bindni AI</p>
              <p className="label-tiny text-brand-gold mt-1">Concierge · Live</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="chatbot-close-btn"><X strokeWidth={1.5} size={20} /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll p-4 space-y-3 bg-brand-ivory-soft">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-brand-maroon text-brand-ivory" : "bg-white border border-brand-gold/25 text-brand-charcoal"}`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={send} className="p-3 border-t border-brand-gold/25 flex gap-2 bg-brand-ivory">
            <input
              value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about shipping, sizing, care…"
              className="flex-1 px-3 py-2.5 text-sm outline-none bg-brand-ivory-soft border border-brand-gold/30 focus:border-brand-gold"
              data-testid="chatbot-input"
              disabled={streaming}
            />
            <button type="submit" disabled={streaming} className="px-3 bg-brand-maroon text-brand-ivory disabled:opacity-50" data-testid="chatbot-send-btn"><Send size={16} strokeWidth={1.5} /></button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
